import { RbacPageName } from 'app/core/rbac/rbac-page-names.const';

export type SlateAudience = 'admin' | 'filmmaker' | 'both';

export type SlateIntentId =
    | 'film_analytics'
    | 'webseries_analytics'
    | 'user_analytics'
    | 'film_stats'
    | 'film_list'
    | 'webseries_list'
    | 'user_list'
    | 'views_report'
    | 'watch_activity'
    | 'purchase_report'
    | 'wallet_balance'
    | 'referral_code'
    | 'help_support'
    | 'open_content'
    | 'film_lookup'
    | 'user_lookup'
    | 'my_access'
    | 'pending_queue'
    | 'audit_recent'
    | 'film_moderation'
    | 'route_guide'
    | 'follow_up_limit'
    | 'follow_up_date'
    | 'out_of_scope'
    | 'clarify';

export type SlateTopCriteria = 'views' | 'uniqueViews' | 'minutesWatched';

export type SlateUserListType =
    | 'FILM_MAKER'
    | 'VIEWER'
    | 'ADMIN'
    | 'AGENT'
    | 'CONTENT_CURATOR';

export interface SlateDateRange {
    startDate: string;
    endDate: string;
    label: string;
    inferredDefault?: boolean;
}

export interface SlateEntities {
    dateRange?: SlateDateRange;
    limit: number;
    topCriteria?: SlateTopCriteria;
    matchParams?: string;
    /** Raw film title query from NL (before ID resolve) */
    filmTitleQuery?: string;
    /** Resolved catalog film id */
    filmId?: string;
    /** Resolved catalog film title */
    filmTitle?: string;
    /** Open film/series command target */
    openMode?: 'view' | 'edit';
    contentKind?: 'film' | 'webseries' | 'unknown';
    seriesId?: string;
    seriesTitle?: string;
    /** Selective film detail fields for film_lookup */
    detailFocus?: Array<
        | 'filmmaker'
        | 'status'
        | 'purchases'
        | 'views'
        | 'revenue'
        | 'price'
        | 'summary'
    >;
    /** User lookup query (phone / email / name) */
    userQuery?: string;
    userQueryKind?: 'phone' | 'email' | 'name';
    userOpen?: boolean;
    status?: string;
    userListType?: SlateUserListType;
    guideId?: string;
    groupBy?: 'day' | 'week' | 'month' | 'year';
    /** What metrics user asked for — drives selective KPI cards */
    metricFocus?: SlateMetricFocus;
    /** Show top films/seasons table only when asked */
    wantTopTable?: boolean;
    /** User wants full dump of metrics */
    wantAllMetrics?: boolean;
    /** Second range for “A vs B” compares */
    compareDateRange?: SlateDateRange;
    /** approve_advice | reject_advice | convert_advice */
    moderationAction?: 'approve_advice' | 'reject_advice' | 'convert_advice';
}

/** Metric slice requested in natural language */
export type SlateMetricFocus =
    | 'views'
    | 'unique_views'
    | 'completed_views'
    | 'incomplete_views'
    | 'minutes'
    | 'purchases'
    | 'revenue'
    | 'filmmaker_revenue'
    | 'boxoffice_revenue'
    | 'new_users'
    | 'active_users'
    | 'cash'
    | 'reel'
    | 'level'
    | 'summary'
    | 'all';

export interface SlateIntentDefinition {
    id: SlateIntentId;
    label: string;
    keywords: string[];
    audience: SlateAudience;
    /** Admin RBAC page required for read; ignored for filmmaker-only intents */
    rbacPage?: RbacPageName;
    weight?: number;
}

export interface SlateIntentMatch {
    intent: SlateMatchedIntent;
    score: number;
}

export interface SlateMatchedIntent {
    id: SlateIntentId;
    label: string;
    rbacPage?: RbacPageName;
    audience: SlateAudience;
}

export interface SlateParseResult {
    kind: 'intent' | 'clarify' | 'out_of_scope' | 'out_of_role' | 'follow_up';
    intentId?: SlateIntentId;
    label?: string;
    entities: SlateEntities;
    candidates?: Array<SlateMatchedIntent>;
    /** Ready-to-send chip prompts (bare name/number / language help) */
    promptChips?: Array<SlateChip>;
    clarifyTitle?: string;
    thinking: string[];
    rbacPage?: RbacPageName;
    guideId?: string;
    message?: string;
}

export type SlateCardType =
    | 'text'
    | 'kpi'
    | 'table'
    | 'guide'
    | 'clarify'
    | 'refuse'
    | 'error';

export interface SlateKpiItem {
    label: string;
    value: string | number;
    /** Show copy control for this value */
    copyable?: boolean;
}

export interface SlateTableColumn {
    key: string;
    label: string;
}

export interface SlateDeepLink {
    label: string;
    route: string;
    queryParams?: Record<string, string>;
    /** If set, button copies this text instead of navigating */
    copyText?: string;
}

/** Human label; optional `prompt` = exact ask sent on tap (may include catalog id). */
export type SlateChip = string | { label: string; prompt?: string };

export function slateChipLabel(chip: SlateChip): string {
    return typeof chip === 'string' ? chip : chip.label;
}

export function slateChipPrompt(chip: SlateChip): string {
    return typeof chip === 'string' ? chip : chip.prompt || chip.label;
}

export interface SlateResponseCard {
    type: SlateCardType;
    title?: string;
    body?: string;
    kpis?: SlateKpiItem[];
    columns?: SlateTableColumn[];
    rows?: Record<string, string | number>[];
    steps?: string[];
    chips?: SlateChip[];
    links?: SlateDeepLink[];
    live?: boolean;
    filterSummary?: string;
    /** Panel navigates to first link after render */
    autoNavigate?: boolean;
    /** Short body when user prefers compact answers */
    shortBody?: string;
    /** Optional mini bars 0–100 for sparkline-lite */
    spark?: number[];
}

export interface SlateMessage {
    id: string;
    role: 'user' | 'assistant';
    text?: string;
    thinking?: string[];
    thinkingDone?: boolean;
    cards?: SlateResponseCard[];
    pending?: boolean;
    createdAt: number;
}

export interface SlateSessionContext {
    lastIntentId?: SlateIntentId;
    lastEntities?: SlateEntities;
    lastRbacPage?: RbacPageName;
    lastLabel?: string;
}
